import { Button, Card, Field, SelectField } from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';

/**
 * Allergies and medications, editable (§3.2, ADR-013 and ADR-014).
 *
 * Both were displayed and neither could be written. `PUT .../allergies` and
 * `PUT .../medications` existed with integration tests and no control anywhere,
 * so the only way a caregiver reference list got any content was the seeder.
 *
 * ## Both are caregiver reference lists, not clinical records
 *
 * ADR-013 and ADR-014 are load-bearing on this screen and the copy says so out
 * loud. An aide **reminds**; they do not administer. Dose and frequency are
 * free text because "half a tablet as needed for pain" has no structured
 * representation that is not a lie, and a form with a numeric dose and a
 * frequency dropdown would make the product look like it understands a
 * prescription. It does not, and it must not appear to.
 *
 * The allergen list is the same argument from the other side: suggestions come
 * from `GET /v1/clients/allergen-suggestions`, and they **offer** rather than
 * constrain. A closed enum would be wrong within a week, so the control is a
 * text input with a `datalist` — type-ahead when the allergen is a common one,
 * and free text when it is not.
 *
 * ## Whole-set PUTs
 *
 * Both endpoints replace the set rather than patching a row, because the intake
 * form shows them together and saves them together. So the editor holds the
 * whole list in a draft and sends all of it, and removing a row is removing it
 * from the draft rather than a DELETE nobody wrote.
 */

const ALLERGY_CATEGORIES = ['DRUG', 'FOOD', 'ENVIRONMENTAL', 'OTHER'] as const;
const ALLERGY_SEVERITIES = ['MILD', 'MODERATE', 'SEVERE', 'ANAPHYLAXIS'] as const;

type AllergyCategory = (typeof ALLERGY_CATEGORIES)[number];
type AllergySeverity = (typeof ALLERGY_SEVERITIES)[number];

export interface AllergyRow {
  id?: string;
  allergen: string;
  category: AllergyCategory;
  severity?: AllergySeverity | null;
  reaction?: string | null;
}

export interface MedicationRow {
  id?: string;
  name: string;
  dose: string;
  frequency: string;
  purpose?: string | null;
}

const LABEL = (value: string): string =>
  value.charAt(0) + value.slice(1).toLowerCase().replace('_', ' ');

/** A row is worth sending once it carries the fields the contract requires. */
export function completeAllergies(rows: readonly AllergyRow[]): AllergyRow[] {
  return rows.filter((row) => row.allergen.trim().length > 0);
}

export function completeMedications(rows: readonly MedicationRow[]): MedicationRow[] {
  return rows.filter(
    (row) =>
      row.name.trim().length > 0 && row.dose.trim().length > 0 && row.frequency.trim().length > 0,
  );
}

// ---------------------------------------------------------------------------

export function AllergiesCard({
  clientId,
  allergies,
  canEdit,
  restricted,
}: {
  clientId: string;
  allergies: AllergyRow[] | undefined;
  canEdit: boolean;
  restricted: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const listId = useId();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AllergyRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  /*
   * Fetched only while the editor is open.
   *
   * The suggestions are a seeded reference list rather than tenant data, so
   * they are cheap and cacheable — but a reader who never edits should not pay
   * a request for a control they will not see.
   */
  const suggestions = useQuery({
    queryKey: ['allergen-suggestions'],
    queryFn: () =>
      api.get<{ suggestions: { label: string; category: string }[] }>(
        '/v1/clients/allergen-suggestions',
      ),
    enabled: editing,
    staleTime: 30 * 60 * 1000,
  });

  const recorded = allergies ?? [];

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      await api.send('PUT', `/v1/clients/${clientId}/allergies`, {
        allergies: completeAllergies(draft).map((row) => ({
          allergen: row.allergen.trim(),
          category: row.category,
          severity: row.severity ?? null,
          reaction: row.reaction?.trim() || null,
        })),
      });

      await queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
      setEditing(false);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Allergies</h2>
          <p className="text-ink-3 mt-1 text-sm">
            A caregiver reference list. Not a medication administration record.
          </p>
        </div>

        {canEdit && !restricted && !editing && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // One blank row when there is nothing yet, because "Add an
              // allergy" that opens an empty panel with an "Add another"
              // button in it is a control that did not do what it said.
              setDraft(
                recorded.length === 0
                  ? [{ allergen: '', category: 'DRUG' }]
                  : recorded.map((row) => ({ ...row })),
              );
              setFailure(null);
              setEditing(true);
            }}
          >
            {recorded.length === 0 ? 'Add an allergy' : 'Edit'}
          </Button>
        )}
      </div>

      {restricted ? (
        <p className="text-ink-3 mt-3 text-sm">
          Hidden. Seeing allergies needs the clinical-records permission.
        </p>
      ) : editing ? (
        <div className="mt-4 grid gap-4" data-testid="allergy-editor">
          <ErrorNote error={failure} nextStep="Check the entries and save again." />

          {/* Offers, never constrains — ADR-013. A `datalist` type-ahead still
              accepts anything typed into it. */}
          <datalist id={listId}>
            {(suggestions.data?.suggestions ?? []).map((entry) => (
              <option key={entry.label} value={entry.label} />
            ))}
          </datalist>

          {draft.map((row, index) => (
            <fieldset key={index} className="border-line grid gap-3 rounded-md border p-3">
              <legend className="text-ink-3 px-1 text-xs">Allergy {index + 1}</legend>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Allergen"
                  list={listId}
                  hint={index === 0 ? 'Start typing — common ones are offered.' : undefined}
                  value={row.allergen}
                  onChange={(event) =>
                    setDraft((was) =>
                      was.map((entry, at) =>
                        at === index ? { ...entry, allergen: event.target.value } : entry,
                      ),
                    )
                  }
                />

                <SelectField
                  label="Category"
                  value={row.category}
                  onChange={(event) =>
                    setDraft((was) =>
                      was.map((entry, at) =>
                        at === index
                          ? { ...entry, category: event.target.value as AllergyCategory }
                          : entry,
                      ),
                    )
                  }
                >
                  {ALLERGY_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {LABEL(category)}
                    </option>
                  ))}
                </SelectField>

                <SelectField
                  label="Severity"
                  value={row.severity ?? ''}
                  onChange={(event) =>
                    setDraft((was) =>
                      was.map((entry, at) =>
                        at === index
                          ? {
                              ...entry,
                              severity: (event.target.value || null) as AllergySeverity | null,
                            }
                          : entry,
                      ),
                    )
                  }
                >
                  <option value="">Not recorded</option>
                  {ALLERGY_SEVERITIES.map((severity) => (
                    <option key={severity} value={severity}>
                      {LABEL(severity)}
                    </option>
                  ))}
                </SelectField>

                <Field
                  label="Reaction"
                  hint={
                    index === 0 ? 'What happens — a rash, swelling, breathing trouble.' : undefined
                  }
                  value={row.reaction ?? ''}
                  onChange={(event) =>
                    setDraft((was) =>
                      was.map((entry, at) =>
                        at === index ? { ...entry, reaction: event.target.value } : entry,
                      ),
                    )
                  }
                />
              </div>

              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDraft((was) => was.filter((_, at) => at !== index))}
                >
                  Remove this allergy
                </Button>
              </div>
            </fieldset>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDraft((was) => [...was, { allergen: '', category: 'DRUG' }])}
            >
              Add another
            </Button>
            <span className="flex-1" />
            <Button onClick={() => void save()} pending={saving}>
              Save allergies
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : recorded.length === 0 ? (
        <p className="text-ink-3 mt-3 text-sm">None recorded.</p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {recorded.map((allergy, index) => (
            <li key={allergy.id ?? index} className="text-ink-2 text-sm">
              <span className="text-ink font-medium">{allergy.allergen}</span>
              {allergy.severity && <span className="text-ink-3"> · {LABEL(allergy.severity)}</span>}
              {allergy.reaction && (
                <span className="text-ink-3 block text-xs">{allergy.reaction}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function MedicationsCard({
  clientId,
  medications,
  canEdit,
  restricted,
}: {
  clientId: string;
  medications: MedicationRow[] | undefined;
  canEdit: boolean;
  restricted: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MedicationRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const recorded = medications ?? [];

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      await api.send('PUT', `/v1/clients/${clientId}/medications`, {
        medications: completeMedications(draft).map((row) => ({
          name: row.name.trim(),
          dose: row.dose.trim(),
          frequency: row.frequency.trim(),
          purpose: row.purpose?.trim() || null,
          isActive: true,
        })),
      });

      await queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
      setEditing(false);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const update = (index: number, patch: Partial<MedicationRow>): void =>
    setDraft((was) => was.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)));

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Medications</h2>
          <p className="text-ink-3 mt-1 text-sm">
            What they take, so a caregiver can remind them. Aides remind; they do not administer.
          </p>
        </div>

        {canEdit && !restricted && !editing && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Same reason as the allergy card: the button says "add".
              setDraft(
                recorded.length === 0
                  ? [{ name: '', dose: '', frequency: '', purpose: '' }]
                  : recorded.map((row) => ({ ...row })),
              );
              setFailure(null);
              setEditing(true);
            }}
          >
            {recorded.length === 0 ? 'Add a medication' : 'Edit'}
          </Button>
        )}
      </div>

      {restricted ? (
        <p className="text-ink-3 mt-3 text-sm">
          Hidden. Seeing medications needs the clinical-records permission.
        </p>
      ) : editing ? (
        <div className="mt-4 grid gap-4" data-testid="medication-editor">
          <ErrorNote error={failure} nextStep="Check the entries and save again." />

          {draft.map((row, index) => (
            <fieldset key={index} className="border-line grid gap-3 rounded-md border p-3">
              <legend className="text-ink-3 px-1 text-xs">Medication {index + 1}</legend>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Medication"
                  value={row.name}
                  onChange={(event) => update(index, { name: event.target.value })}
                />
                <Field
                  label="Purpose"
                  hint={index === 0 ? 'Optional — what it is for.' : undefined}
                  value={row.purpose ?? ''}
                  onChange={(event) => update(index, { purpose: event.target.value })}
                />
                <Field
                  label="Dose"
                  // Free text, ADR-014. "Half a tablet in the morning" and "as
                  // needed for pain" are what people write, and a numeric field
                  // with a unit dropdown would refuse both.
                  hint={index === 0 ? 'As it is written — "10 mg", "half a tablet".' : undefined}
                  value={row.dose}
                  onChange={(event) => update(index, { dose: event.target.value })}
                />
                <Field
                  label="Frequency"
                  hint={index === 0 ? 'As it is written — "twice daily with meals".' : undefined}
                  value={row.frequency}
                  onChange={(event) => update(index, { frequency: event.target.value })}
                />
              </div>

              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDraft((was) => was.filter((_, at) => at !== index))}
                >
                  Remove this medication
                </Button>
              </div>
            </fieldset>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setDraft((was) => [...was, { name: '', dose: '', frequency: '', purpose: '' }])
              }
            >
              Add another
            </Button>
            <span className="flex-1" />
            <Button onClick={() => void save()} pending={saving}>
              Save medications
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : recorded.length === 0 ? (
        <p className="text-ink-3 mt-3 text-sm">None recorded.</p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {recorded.map((medication, index) => (
            <li key={medication.id ?? index} className="text-ink-2 text-sm">
              <span className="text-ink font-medium">{medication.name}</span>
              {medication.dose && <span> · {medication.dose}</span>}
              {medication.frequency && <span> · {medication.frequency}</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
